import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import ScrollToPlugin from 'gsap/ScrollToPlugin';

let smoother = null;

export function initScroll() {

    gsap.registerPlugin(ScrollTrigger, ScrollSmoother, ScrollToPlugin);

    // Create or use existing ScrollSmoother instance
    smoother = ScrollSmoother.create({
        wrapper: '#smooth-wrapper',
        content: '#smooth-content',
        smooth: 2,
        speed: 1.2,
        effects: true,
        smoothTouch: 0.1,
    });

    // Refresh triggers once above setup completes
    ScrollTrigger.refresh();

    // Debounced refresh function to prevent excessive refreshes
    let refreshTimeout = null;
    function debouncedRefresh() {
        if (refreshTimeout) {
            clearTimeout(refreshTimeout);
        }
        refreshTimeout = setTimeout(() => {
            if (smoother) {
                ScrollTrigger.refresh();
                // Also refresh smoother to recalculate heights
                smoother.refresh();
            }
        }, 150);
    }

    // Refresh on lazyload completion for image adjustments
    document.addEventListener('lazyloaded', () => {
        debouncedRefresh();
    });

    // Refresh on window resize
    let resizeTimeout = null;
    window.addEventListener('resize', () => {
        if (resizeTimeout) {
            clearTimeout(resizeTimeout);
        }
        resizeTimeout = setTimeout(() => {
            debouncedRefresh();
        }, 250);
    });

    // Refresh on orientation change (mobile devices)
    window.addEventListener('orientationchange', () => {
        setTimeout(() => {
            debouncedRefresh();
        }, 500); // Wait a bit longer for orientation change to complete
    });

    // Refresh when images load (in case lazy loading is disabled or images load normally)
    const images = document.querySelectorAll('img');
    images.forEach((img) => {
        if (img.complete) {
            // Image already loaded
            debouncedRefresh();
        } else {
            img.addEventListener('load', debouncedRefresh, { once: true });
            img.addEventListener('error', debouncedRefresh, { once: true });
        }
    });

    // Refresh when fonts are loaded (can affect layout)
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
            debouncedRefresh();
        });
    }

    // Refresh when content is dynamically added (MutationObserver)
    const observer = new MutationObserver((mutations) => {
        let shouldRefresh = false;
        mutations.forEach((mutation) => {
            // Check if nodes were added/removed or attributes changed that might affect layout
            if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
                shouldRefresh = true;
            }
            if (mutation.type === 'attributes' && 
                (mutation.attributeName === 'style' || 
                 mutation.attributeName === 'class' ||
                 mutation.attributeName === 'src')) {
                shouldRefresh = true;
            }
        });
        if (shouldRefresh) {
            debouncedRefresh();
        }
    });

    // Observe changes to the smooth-content wrapper
    const smoothContent = document.querySelector('#smooth-content');
    if (smoothContent) {
        observer.observe(smoothContent, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class', 'src'],
        });
    }

    // Function to scroll to target with smooth animation
    function scrollToTarget(selector, offset = 76) {
        if (!smoother) {
            console.warn('ScrollSmoother not initialized'); // eslint-disable-line no-console
            return;
        }

        const targetElement = document.querySelector(selector);
        if (targetElement) {
            smoother.scrollTo(selector, true, `top ${offset}px`);
        } else {
            console.warn('Target element not found for:', selector); // eslint-disable-line no-console
        }
    }

    // Handle anchor link clicks
    jQuery(document).on('click', "a[href*='#']", function (event) {
        const href = jQuery(this).attr('href');
        
        if (href.startsWith('/#')) {
            // External link with hash - let browser handle navigation
            window.location.href = href;
            return;
        } else if (href.startsWith('#')) {
            // Handle internal anchors on same page
            event.preventDefault();
    
            // Close mobile menu if open
            jQuery('body').removeClass('show-mobile-menu');
            
            // Scroll to target with smooth animation
            scrollToTarget(href, 76);
        }
    });

    // Handle hash in URL on page load
    function handleInitialHash() {
        const hash = window.location.hash;
        if (hash) {
            // Wait for ScrollSmoother to be ready and DOM to be fully loaded
            setTimeout(() => {
                ScrollTrigger.refresh();
                scrollToTarget(hash, 100);
            }, 100);
        }
    }

    // Handle initial hash after ScrollSmoother is ready
    if (document.readyState === 'complete') {
        handleInitialHash();
    } else {
        window.addEventListener('load', handleInitialHash);
    }

    // Also handle hash changes (browser back/forward)
    window.addEventListener('hashchange', function() {
        const hash = window.location.hash;
        if (hash) {
            setTimeout(() => {
                ScrollTrigger.refresh();
                scrollToTarget(hash, 100);
            }, 100);
        }
    });

}